/**
 * Appointment API Client
 * 예약 관련 API 호출
 */

import apiClient from "./client";
import type {
  AppointmentTimeline,
  AppointmentStatusHistory,
} from "@/types/appointment";

export interface AppointmentDailySummary {
  date: string;
  totalCount: number;
  pendingCount: number;
  confirmedCount: number;
  cancelledCount: number;
  noShowCount: number;
  completedCount: number;
}

/**
 * V1050: 수기 예약 생성 요청
 */
export interface ManualAppointmentRequest {
  customerPhone: string;
  customerName?: string;
  appointmentDate: string; // YYYY-MM-DD
  appointmentTime: string; // HH:mm
  serviceId?: number;
  serviceName?: string;
  subItemId?: number;
  subItemName?: string;
  medicalStaffId?: number; // 담당 의료진 ID (선택)
  memo?: string;
  source?: "PHONE" | "WALK_IN" | "MANUAL";
  consultId?: number; // 기존 상담에 연결 (상담 상세에서 수기 예약 시)
}

/**
 * BL-120: 관리자용 날짜별 예약 가능 슬롯
 */
export interface SlotInfo {
  time: string;
  available: boolean;
  remainingSlots: number;
  bookedCount: number;
  maxSlots: number;
  reason?: string;
  closed?: boolean;
}

export const appointmentApi = {
  /**
   * Phase 0D2: 예약 현황 검색 (Appointment 직접 조회)
   * GET /api/appointments/search
   */
  searchAppointments: async (params: {
    from: string;
    to: string;
    status?: string[];
    q?: string;
    sort?: string;
  }): Promise<AppointmentTimeline[]> => {
    const queryParams = new URLSearchParams();
    queryParams.append("from", params.from);
    queryParams.append("to", params.to);
    if (params.status?.length)
      params.status.forEach((s) => queryParams.append("status", s));
    if (params.q) queryParams.append("q", params.q);
    if (params.sort) queryParams.append("sort", params.sort);
    const response = await apiClient.get<AppointmentTimeline[]>(
      `/api/appointments/search?${queryParams.toString()}`,
    );
    return response.data;
  },

  /**
   * BL-120: 날짜별 예약 가능 슬롯 조회 (관리자용)
   * GET /api/appointments/available-slots?date=YYYY-MM-DD
   */
  getAvailableSlots: async (date: string): Promise<SlotInfo[]> => {
    const response = await apiClient.get<SlotInfo[]>(
      "/api/appointments/available-slots",
      { params: { date } },
    );
    return response.data;
  },

  /**
   * V1050: 수기 예약 생성
   * POST /api/appointments
   */
  createManual: async (
    request: ManualAppointmentRequest,
  ): Promise<AppointmentTimeline> => {
    const response = await apiClient.post<AppointmentTimeline>(
      "/api/appointments",
      request,
    );
    return response.data;
  },

  /**
   * Consult ID로 연결된 예약 목록 조회
   * GET /api/appointments/by-consult/{consultId}
   */
  getByConsultId: async (consultId: number): Promise<AppointmentTimeline[]> => {
    const response = await apiClient.get<AppointmentTimeline[]>(
      `/api/appointments/by-consult/${consultId}`,
    );
    return response.data;
  },

  /**
   * 예약 단건 조회 (Appointment ID)
   * GET /api/appointments/{id}
   */
  getById: async (id: number): Promise<AppointmentTimeline> => {
    const response = await apiClient.get<AppointmentTimeline>(
      `/api/appointments/${id}`,
    );
    return response.data;
  },

  /**
   * V1096: UUID 기반 예약 상세 조회
   * GET /api/appointments/by-uuid/{uuid}
   */
  getByUuid: async (uuid: string): Promise<AppointmentTimeline> => {
    const response = await apiClient.get<AppointmentTimeline>(
      `/api/appointments/by-uuid/${uuid}`,
    );
    return response.data;
  },

  /**
   * 월간 예약 요약 조회
   * GET /api/appointments/summary?year={year}&month={month}
   */
  getSummary: async (
    year: number,
    month: number,
  ): Promise<AppointmentDailySummary[]> => {
    const response = await apiClient.get<AppointmentDailySummary[]>(
      "/api/appointments/summary",
      {
        params: { year, month },
      },
    );
    return response.data;
  },

  /**
   * 예약 타임라인 조회
   * GET /api/appointments/timeline?date=YYYY-MM-DD&department={department}
   * @param date - 조회할 날짜 (YYYY-MM-DD)
   * @param department - 진료과목 필터 (선택)
   */
  getTimeline: async (
    date: string,
    department?: string,
  ): Promise<AppointmentTimeline[]> => {
    const params = new URLSearchParams({ date });
    if (department && department !== "all") {
      params.append("department", department);
    }

    const response = await apiClient.get<AppointmentTimeline[]>(
      `/api/appointments/timeline?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * 예약 승인
   * PUT /api/appointments/{id}/approve?selectedPriority={priority}
   * Phase 15: selectedPriority 파라미터 추가
   * @param id - 상담 ID
   * @param selectedPriority - 선택된 우선순위 (1 or 2, undefined면 자동 선택)
   */
  approve: async (id: number, selectedPriority?: number): Promise<void> => {
    const params = selectedPriority ? { selectedPriority } : {};
    await apiClient.put(`/api/appointments/${id}/approve`, null, { params });
  },

  /**
   * 예약 거부
   * PUT /api/appointments/{id}/reject
   * @param id - 상담 ID
   * @param reason - 거부 사유 (선택)
   */
  reject: async (id: number, reason?: string): Promise<void> => {
    await apiClient.put(`/api/appointments/${id}/reject`, { reason });
  },

  /**
   * 예약 상태 변경 (노쇼, 이용완료, 취소 등)
   * PUT /api/appointments/{id}/status
   * @param id - 상담 ID
   * @param status - 변경할 상태 (NO_SHOW, COMPLETED, CANCELLED)
   * @param reason - 사유 (선택)
   */
  updateStatus: async (
    id: number,
    status: string,
    reason?: string,
  ): Promise<void> => {
    await apiClient.put(`/api/appointments/${id}/status`, { status, reason });
  },

  /**
   * 노쇼 처리
   * PUT /api/appointments/{id}/no-show
   * @param id - 상담 ID
   */
  markNoShow: async (id: number): Promise<void> => {
    await apiClient.put(`/api/appointments/${id}/no-show`);
  },

  /**
   * 이용완료 처리
   * PUT /api/appointments/{id}/complete
   * @param id - 상담 ID
   */
  markComplete: async (id: number): Promise<void> => {
    await apiClient.put(`/api/appointments/${id}/complete`);
  },

  /**
   * 예약 취소
   * PUT /api/appointments/{id}/cancel
   * @param id - 상담 ID
   * @param reason - 취소 사유 (선택)
   */
  cancel: async (id: number, reason?: string): Promise<void> => {
    await apiClient.put(`/api/appointments/${id}/cancel`, { reason });
  },

  /**
   * 예약 변경 (날짜/시간/상품)
   * PUT /api/appointments/{id}/reschedule
   */
  reschedule: async (
    id: number,
    newDate: string,
    newTime: string,
    reason?: string,
    serviceId?: number | null,
    subItemId?: number | null,
  ): Promise<void> => {
    await apiClient.put(`/api/appointments/${id}/reschedule`, {
      newDate,
      newTime,
      reason,
      serviceId,
      subItemId,
    });
  },

  /**
   * 예약 진행이력 조회
   * GET /api/appointments/{id}/history
   * Phase 2-3: 진행이력 타임라인
   * @param id - 상담 ID
   */
  getHistory: async (id: number): Promise<AppointmentStatusHistory[]> => {
    const response = await apiClient.get<AppointmentStatusHistory[]>(
      `/api/appointments/${id}/history`,
    );
    return response.data;
  },

  /**
   * 담당 의료진 배정/변경
   * PUT /api/appointments/{id}/assign-staff
   * @param id - 상담 ID
   * @param staffId - 의료진 ID (null이면 배정 해제)
   */
  assignStaff: async (
    id: number,
    staffId: number | null,
  ): Promise<AppointmentTimeline> => {
    const response = await apiClient.put<AppointmentTimeline>(
      `/api/appointments/${id}/assign-staff`,
      { medicalStaffId: staffId },
    );
    return response.data;
  },
};

/**
 * 예약 설정 타입
 * @deprecated V1069: 예약 규칙은 BookingRuleDto (bookingRulesApi)를 사용하세요.
 * 이 타입은 슬롯 오버라이드 API 호환을 위해 유지됩니다.
 */
export interface BookingSettings {
  maxBookingsPerSlot: number;
  maxBookingsPerTimeSlot: number;
  slotIntervalMinutes: number;
  bookingStartTime: string;
  bookingEndTime: string;
}

/**
 * SlotOverride - 날짜별 서비스 슬롯 설정
 * V205: isServiceHidden 추가 (날짜별 서비스 노출/미노출)
 * V1265: serviceId null/생략 → 전 시술 공통 마감 (시술 무관)
 */
export interface SlotOverride {
  /** 서비스 이름. null/생략 시 전 시술 공통 마감 행 */
  serviceId?: string | null;
  disabledSlots: Record<string, boolean>;
  slotCapacities?: Record<string, number>;
  isFullDayClosed: boolean;
  /** V205: 해당 날짜에서 이 서비스가 미노출인지 여부 */
  isServiceHidden?: boolean;
}

export interface SaveBookingSettingsRequest {
  date: string;
  maxBookingsPerSlot?: number;
  maxBookingsPerTimeSlot?: number;
  slots: SlotOverride[];
}

/**
 * 예약 슬롯 오버라이드 API
 * V1069: 예약 규칙(booking rules)은 bookingRulesApi로 이관됨.
 * 이 API는 날짜별 슬롯 마감/인원 설정(booking_slot_overrides)만 관리합니다.
 */
export const bookingSettingsApi = {
  /**
   * 날짜별 슬롯 마감 설정 조회
   * GET /api/booking-settings/slots?date=YYYY-MM-DD
   */
  getSlotOverrides: async (date: string): Promise<SlotOverride[]> => {
    const response = await apiClient.get<SlotOverride[]>(
      "/api/booking-settings/slots",
      {
        params: { date },
      },
    );
    return response.data;
  },

  /**
   * 예약 설정 전체 저장 (기본 설정 + 날짜별 슬롯 마감)
   * POST /api/booking-settings/save-all
   */
  saveAll: async (request: SaveBookingSettingsRequest): Promise<void> => {
    await apiClient.post("/api/booking-settings/save-all", request);
  },
};

/**
 * 시간대 반복 마감(recurring block) 요청
 * 특정 서비스의 시간대를 모든 날짜(또는 특정 요일)에 일괄 마감하는 규칙.
 * @remarks serviceId는 legacy 관례대로 서비스 "이름" 문자열 (SlotOverride와 동일).
 */
export interface RecurringBlockRequest {
  /** 서비스 식별자 (서비스 이름 문자열) */
  serviceId: string;
  /** 반복 요일: null=매일, 1~7=해당 요일(ISO 1=월 .. 7=일) */
  dayOfWeek: number | null;
  /** 마감 시작 시각 (HH:mm) */
  startTime: string;
  /** 마감 종료 시각 (HH:mm, 반열림 [start, end)) */
  endTime: string;
}

/**
 * 시간대 반복 마감 규칙 (목록 표시용)
 */
export interface RecurringBlock {
  id: number;
  serviceId: string;
  /** null=매일, 1~7=해당 요일 */
  dayOfWeek: number | null;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

/**
 * 시간대 반복 마감 API
 * RBAC: 조회 ORG_ADMIN/POWER/USER, 생성·삭제 ORG_ADMIN/POWER.
 * POST   /api/booking-settings/recurring-blocks      — 규칙 생성
 * GET    /api/booking-settings/recurring-blocks      — 규칙 목록
 * DELETE /api/booking-settings/recurring-blocks/{id} — 규칙 삭제
 */
export const recurringBlocksApi = {
  create: async (request: RecurringBlockRequest): Promise<RecurringBlock> => {
    const response = await apiClient.post<RecurringBlock>(
      "/api/booking-settings/recurring-blocks",
      request,
    );
    return response.data;
  },

  list: async (): Promise<RecurringBlock[]> => {
    const response = await apiClient.get<RecurringBlock[]>(
      "/api/booking-settings/recurring-blocks",
    );
    return response.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/booking-settings/recurring-blocks/${id}`);
  },
};
